import {
  BillRowVm,
  BillSectionVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';

/**
 * Przejście z „po zmianach" na „przed zmianami".
 *
 * Model widoku niesie zawsze stan *po* uwzględnieniu szkicu (`value`,
 * `amount`) plus stan sprzed (`oldValue`, `oldAmount`). Podgląd i edycja to ten
 * sam układ — różnią się wyłącznie tym, którą warstwę pokazujemy. Zamiast
 * rozsypywać `isEditing ? … : …` po każdej komórce wiersza, cała transformacja
 * siedzi tutaj, a komponenty dostają już gotowe wiersze.
 */

/**
 * Wiersz w wersji sprzed zmian: wartość i kwota cofnięte do stanu wyjściowego,
 * chipy dochodzące w szkicu odfiltrowane, znaczniki szkicu (kropka zmiany,
 * ✎ nadpisanej kwoty, przekreślone stare wartości) zgaszone.
 */
export const toPreviewRow = (row: BillRowVm): BillRowVm => ({
  ...row,
  // Mapper podaje komplet prezentacji sprzed zmian; gdy go nie ma (wiersz nie
  // jest w szkicu albo pochodzi z testu), wystarczy cofnąć wartość i kwotę.
  ...(row.before ?? {
    value: row.oldValue ?? row.value,
    chips: row.chips?.filter((chip) => !chip.added),
    amount: row.oldAmount.length > 0 ? row.oldAmount : row.amount,
  }),
  oldValue: undefined,
  oldAmount: [],
  before: undefined,
  changed: false,
  overridden: false,
});

/**
 * Sekcja w wersji sprzed zmian — suma spada do `sumBefore`, wiersze do stanu
 * wyjściowego, a przypis znika: mówi o skutku wprowadzenia zmiany, więc
 * w podglądzie nie ma do czego się odnosić.
 *
 * Wiersze dopisane przez sam szkic (pozycje ręczne) wypadają w całości —
 * w stanie sprzed zmian nie ma czego pokazać, a pusta linijka z myślnikiem
 * udawałaby pozycję, której rachunek nie zna.
 */
export const toPreviewSection = (section: BillSectionVm): BillSectionVm => ({
  ...section,
  sum: section.sumBefore,
  sumBefore: [],
  note: undefined,
  rows: section.rows.filter((row) => !row.draftOnly).map(toPreviewRow),
});

/**
 * Sekcje poza zasięgiem filtra „Tylko zmienione" — takie, których wierszy
 * operator nie edytuje z tego ekranu (`locked`: zniżki sterowane z rezerwacji)
 * albo które sam dopisał (`manual`: wolne linie). W projekcie chodzi o blok
 * „Zniżki i korekty" i „Pozycje ręczne"; rozpoznajemy je po wierszach, a nie po
 * tytule, żeby filtr nie zależał od napisu.
 */
const isFilterExempt = (section: BillSectionVm): boolean =>
  section.rows.length > 0 &&
  section.rows.every((row) => Boolean(row.locked) || Boolean(row.manual));

export interface PrepareSectionsOptions {
  isEditing: boolean;
  /** Filtr „Tylko zmienione"; działa wyłącznie w trybie edycji. */
  onlyChanged?: boolean;
}

/**
 * Lista sekcji gotowa do renderowania: najpierw filtr „Tylko zmienione",
 * potem — w podglądzie — cofnięcie do stanu sprzed zmian.
 *
 * Sekcja, której nie zostaje żaden wiersz, znika. Wyjątkiem jest sekcja
 * przyjmująca wolne linie w trybie edycji — ta zostaje, bo niesie przycisk
 * „+ Wolna linia".
 */
export const prepareSections = (
  sections: BillSectionVm[],
  { isEditing, onlyChanged = false }: PrepareSectionsOptions,
): BillSectionVm[] =>
  sections
    .map((section) => {
      const filtered =
        isEditing && onlyChanged && !isFilterExempt(section)
          ? {
              ...section,
              rows: section.rows.filter((row) => row.changed),
              // Suma sekcji dotyczy całej sekcji, nie tego, co zostawił filtr —
              // bez tej liczby jeden widoczny wiersz gasiłby sumę przy nagłówku.
              totalRowCount: section.rows.length,
            }
          : section;

      return isEditing ? filtered : toPreviewSection(filtered);
    })
    .filter(
      (section) =>
        section.rows.length > 0 || (isEditing && Boolean(section.allowAdd)),
    );
